import Bugsnag from "@bugsnag/js";
import axios from "axios";
import dayjs from "dayjs";
import 'dayjs/locale/en';
import 'dayjs/locale/et';
import 'dayjs/locale/it';
import 'dayjs/locale/pt';
import 'dayjs/locale/ru';
import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from "react-helmet";
import { getBrand } from "../branding";
import { useTranslation } from "react-i18next";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import ConfirmEmail from "../components/pages/ConfirmEmail";

import { LoginPage } from "../components/pages/LoginPage";
import ResetPassword from "../components/pages/ResetPassword";

import { firebaseAuth, getErrorMessage } from "../firebase";
import i18next from '../i18n';
import ability, { roles } from "../services/ability";
import { AbilityContext } from "../services/Can";

import { get, getUrl, put, USERS } from "../services/Client";
import { EmailConfirmedPage } from "./EmailConfirmedPage";
import FirebaseActionHandler from "./FirebaseActionHandler";
import ConfirmFormComplete from "./Forms/ConfirmFormComplete";
import Main from "./Main";
import MainVendor from "./MainVendor";
import OpenDataPage from './OpenData/OpenDataPage';
import { PaddingContainer } from "./PaddingContainer";
import CouponRedeem from "./Vendor/CouponRedeem/CouponRedeem";
import NewVendor from "./Vendor/NewVendor/NewVendor";
import OpenDataUser from "./OpenData/OpenDataUser";
import PedalSharePage, { MONITOR_PATH, monitorUrl } from "./Pinbiker/PedalSharePage";
import PedalShareCodePage from "./Pinbiker/PedalShareCodePage";
import MainPinbiker from "./MainPinbiker";
import { SupportHelpPage } from "./Vendor/HelpPage";
import NewPinbiker from "./Pinbiker/NewPinbiker";
import ConnectPage from "./Connect/ConnectPage";

// Firebase 12 modulare
import {
    onAuthStateChanged,
    signOut,
    sendPasswordResetEmail,
} from "firebase/auth";

export const UserContext = React.createContext({});

function checkUser(user, setUser, setError) {
    if (user && user.uid) {
        get(USERS, { elem: user.uid }).then(userData => {
            if (!user.emailVerified) {
                setUser({ isLogged: false, ready: true });
                setError(i18next.t('verifyEmail'));
                signOut(firebaseAuth);
            } else if (userData.userType === roles.PINBIKER && userData.signupCompleted === false) {
                /**
                 * Registrazione avviata ma non completata → torna a /newPinbiker.
                 * Il gate vale SOLO per i pinbiker: signupCompleted è un concetto della
                 * registrazione pinbiker e può risultare false anche su utenti vendor/manager
                 * Non facciamo signOut: l'utente resta loggato in Firebase
                 */
                setUser({ isLogged: false, ready: true, signupIncomplete: true });
            } else {
                put(USERS, {
                    elem: userData.uid,
                    body: { lastLoggedIn: new Date().getTime(), resetPasswordRequired: false }
                }).catch(console.log);
                Bugsnag.setUser(userData.uid, userData.email, userData.username);
                setUser({ ...userData, isLogged: true, ready: true });
            }
        }).catch(() => {
            // Utente Firebase esiste ma non ha ancora un profilo Pinbike → avvia registrazione
            setUser({ isLogged: false, ready: true, signupIncomplete: true });
        });
    } else {
        setUser({ isLogged: false, ready: true });
    }
}

function App() {
    dayjs.locale(i18next.resolvedLanguage);

    const { t } = useTranslation();
    let [user, setUser] = useState({ ready: false });
    let [error, setError] = useState(null);
    let [username, setUsername] = useState("");
    let history = useHistory();

    useEffect(() => {
        // Firebase 12: languageCode si imposta così
        firebaseAuth.languageCode = i18next.language || 'en';
    }, [i18next.language]);

    useEffect(() => {
        // Firebase 12: onAuthStateChanged è una funzione modulare
        const unsubscribe = onAuthStateChanged(firebaseAuth, user => {
            checkUser(user, setUser, setError);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        // Durante la registrazione dell'esercente l'utente Firebase nasce prima del profilo: il 401 di
        // GET /users non vuol dire "pinbiker a meta'", e il form esercente prosegue da se' fino a /confirmEmail
        if (user.signupIncomplete && !history.location.pathname.startsWith('/newVendor')) history.push('/newPinbiker');
    }, [user.signupIncomplete]);


    function onLogin(username, password) {
        // Questo viene chiamato da LoginPage SOLO per gli errori normali
        // (il signInWithEmailAndPassword è già in LoginPage per gestire il 2FA)
        // Qui gestiamo solo il caso del reset password obbligatorio
        axios.get(getUrl(`${USERS}/${username}/reset-password`))
            .then(r => {
                if (r.data) {
                    sendPasswordResetEmail(firebaseAuth, username)
                        .then(() => {
                            setUsername(username);
                            history.push("/resetPassword");
                        })
                        .catch(() => setError(t('internalServerError')));
                }
            })
            .catch(() => {});
        // L'errore visivo viene già gestito in LoginPage tramite externalError
        // ma per sicurezza impostiamo anche qui un messaggio generico
        setError(t('wrongPassword'));
    }

    function onResetPassword(username) {
        sendPasswordResetEmail(firebaseAuth, username)
            .then(() => {
                setUsername(username);
                history.push("/resetPassword");
            })
            .catch(e => setError(getErrorMessage(e)));
    }

    if (!user.ready) return <div />;

    return (
        <UserContext.Provider value={user}>
            <Helmet>
                <meta charSet="utf-8" />
                <title>{getBrand().name}</title>
                <meta name="description" content="Pin Bike application" />
            </Helmet>
            <Switch>
                <UnloggedRoute path="/login" setError={setError} redirect>
                    <LoginPage onSubmit={onLogin} onResetPassword={onResetPassword}
                        error={error} />
                </UnloggedRoute>
                <UnloggedRoute path="/resetPassword">
                    <ResetPassword username={username} />
                </UnloggedRoute>
                <UnloggedRoute path="/newVendor" setError={setError} redirect>
                    <NewVendor />
                </UnloggedRoute>
                <UnloggedRoute path="/newPinbiker" setError={setError}>
                    <NewPinbiker />
                </UnloggedRoute>
                <UnloggedRoute path="/connect" setError={setError}>
                    <ConnectPage />
                </UnloggedRoute>
                <UnloggedRoute path="/confirmEmail" setError={setError} redirect>
                    <ConfirmEmail />
                </UnloggedRoute>
                <UnloggedRoute path="/confirmFormComplete/:aid" setError={setError}>
                    <ConfirmFormComplete />
                </UnloggedRoute>
                <UnloggedRoute path="/emailValidated" setError={setError}>
                    <EmailConfirmedPage />
                </UnloggedRoute>
                <UnloggedRoute path="/action" setError={setError}>
                    <FirebaseActionHandler />
                </UnloggedRoute>
                <UnloggedRoute path="/riscatta/:publicLink" setError={setError}>
                    <PaddingContainer><CouponRedeem auto /></PaddingContainer>
                </UnloggedRoute>
                <UnloggedRoute path="/support" setError={setError}>
                    <SupportHelpPage />
                </UnloggedRoute>
                <UnloggedRoute path="/open/:id/:variantCode?">
                    <OpenDataPage />
                </UnloggedRoute>
                <UnloggedRoute exact path={MONITOR_PATH}>
                    <PedalShareCodePage />
                </UnloggedRoute>
                <UnloggedRoute path={`${MONITOR_PATH}/:token`}>
                    <PedalSharePage />
                </UnloggedRoute>
                {/* Vecchio percorso dei link condivisi: rimanda al nuovo, token compreso. */}
                <Route path="/pedale/:token?" render={({ match }) =>
                    <Redirect to={match.params.token ? monitorUrl(match.params.token) : MONITOR_PATH} />} />
                <AbilityContext.Provider value={ability(user)}>
                    <PrivateRoute path="/">
                        {
                            user.userType === roles.VENDOR ?
                                <MainVendor />
                                :
                                user.userType === roles.PINBIKER ?
                                    <MainPinbiker />
                                    :
                                    <Main />
                        }
                    </PrivateRoute>
                </AbilityContext.Provider>
            </Switch>
        </UserContext.Provider>
    );
}

function PrivateRoute({ children, ...rest }) {
    let user = useContext(UserContext) || {};
    return (
        <Route
            {...rest}
            render={({ location }) =>
                user.isLogged ? (
                    children
                ) : (
                    <Redirect to={{ pathname: "/login", state: { from: location } }} />
                )
            }
        />
    );
}

function UnloggedRoute({ redirect, children, setError, ...rest }) {
    const user = useContext(UserContext) || {};
    return (
        <Route
            {...rest}
            render={() =>
                user.isLogged && redirect ? (
                    <Redirect to={{ pathname: "/" }} />
                ) : (
                    children
                )
            }
        />
    );
}

export default App;